import { Group, Tooltip, useMantineTheme } from '@mantine/core';
import P3Regular from 'components/typography/P3Regular';
import P2Regular from 'components/typography/P2Regular';
import P2Medium from 'components/typography/P2Medium';
import DeviceStructureIcon from 'components/DeviceStructureIcon';
import { FEATURE_TOGGLE_DEVICE_IMAGES_SLIDER } from 'env';
import DeviceImage from 'components/DeviceImage';
import { IconTrashX } from '@tabler/icons-react';
import DeviceTypeIcon from 'components/device/DeviceTypeIcon';

interface DeviceNameBreadcrumbsProps {
  deviceId: number;
  imageFiles?: {
    fileId: string;
    isMainImage: boolean;
  }[];
  deviceType: {
    deviceTypeId: number;
  };
  structure: string;
  deviceName: string;
  status: boolean;
  parentDevice?: {
    deviceId: number;
    deviceName: string;
  };
  withDeviceImage?: boolean;
  withTypeIcon?: boolean;
}

/**
 * Displays the device name with breadcrumbs for parent devices and an icon for the device structure.
 */
export default function DeviceNameBreadcrumbs({
  imageFiles,
  deviceType,
  structure,
  deviceName,
  status,
  parentDevice,
  withDeviceImage,
  withTypeIcon,
}: DeviceNameBreadcrumbsProps) {
  const theme = useMantineTheme();

  return (
    <Group spacing={8} className="gap-[0_8px]" noWrap align="center">
      {FEATURE_TOGGLE_DEVICE_IMAGES_SLIDER && withDeviceImage && <DeviceImage imageFiles={imageFiles} />}
      {withTypeIcon && <DeviceTypeIcon typeId={deviceType.deviceTypeId} />}

      {!status && (
        <Tooltip label="Vyradené zariadenie" openDelay={200} position="top" withinPortal>
          <IconTrashX size={20} color={theme.colors.red[8]} />
        </Tooltip>
      )}

      <Group spacing={8} className="gap-[0_8px]">
        {/* Parent device */}
        {!!parentDevice && (
          <Tooltip label={<P3Regular>ID: {parentDevice.deviceId}</P3Regular>} position="top" offset={4} openDelay={200}>
            <Group spacing={8} className="gap-[0_8px]" noWrap>
              <P2Regular color="gray.6" className="underline decoration-dotted">
                {parentDevice.deviceName}
              </P2Regular>
              <P2Regular color="gray.6">/</P2Regular>
            </Group>
          </Tooltip>
        )}

        {/* This device */}
        <Group spacing={8} className="gap-[0_8px]" noWrap>
          <P2Medium color="gray.8" strikethrough={!status} opacity={status ? 1 : 0.6}>
            {deviceName}
          </P2Medium>
          <DeviceStructureIcon structure={structure} />
        </Group>
      </Group>
    </Group>
  );
}
